import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { NavBar } from "@/components/NavBar";
import { api, fmt, hhmm } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Clock, Coins, Trophy, Gift, Megaphone } from "lucide-react";

const Stat = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="card-hover border border-border bg-card p-6" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{label}</span>
      <Icon className="h-4 w-4 text-primary" />
    </div>
    <div className="mono mt-4 text-3xl font-semibold">{value}</div>
    {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
  </div>
);

export default function PlayerDashboard() {
  const { setUser } = useAuth();
  const [s, setS] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [txs, setTxs] = useState([]);
  const [offers, setOffers] = useState([]);

  const load = useCallback(async () => {
    const [a, b, c, d] = await Promise.all([
      api.get("/me/summary"),
      api.get("/me/sessions"),
      api.get("/me/transactions"),
      api.get("/offers"),
    ]);
    setS(a.data);
    setUser(a.data.user);
    setSessions(b.data);
    setTxs(c.data);
    setOffers(d.data);
  }, [setUser]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  if (!s)
    return (
      <div className="min-h-screen">
        <NavBar />
        <div className="p-12 text-muted-foreground" data-testid="dashboard-loading">Loading…</div>
      </div>
    );

  const u = s.user;

  return (
    <div className="min-h-screen">
      <NavBar />
      <main className="mx-auto max-w-7xl px-6 py-12" data-testid="player-dashboard">
        <div className="rise flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-primary">Level {s.level.level} · {s.level.name}</p>
            <h1 className="mt-2 text-4xl font-bold">{u.username}</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Rank #{s.rank} on the server · perk: {s.level.perk}
            </p>
          </div>
        </div>

        <div className="mt-10 grid gap-4 md:grid-cols-4">
          <Stat icon={Coins} label="Point balance" value={fmt(u.points_balance)} sub="spendable now" testid="stat-balance" />
          <Stat icon={Clock} label="Playtime" value={hhmm(u.playtime_minutes)} sub={`${fmt(u.lifetime_points)} lifetime points`} testid="stat-playtime" />
          <Stat icon={Trophy} label="Achievements" value={`${s.achievements_unlocked}/${s.achievements_total}`} sub="unlocked" testid="stat-achievements" />
          <Stat icon={Gift} label="Pending rewards" value={fmt(s.pending_redemptions)} sub="awaiting in-game delivery" testid="stat-pending" />
        </div>

        <div className="mt-4 border border-border bg-card p-8" data-testid="level-progress-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Rank progress</span>
            <span className="mono text-sm text-primary">
              {s.next_level ? `${fmt(s.points_to_next)} pts to ${s.next_level.name}` : "Max rank reached"}
            </span>
          </div>
          <div className="mt-5 h-2 w-full bg-secondary">
            <div
              className="h-2 bg-primary shadow-[0_0_12px_hsl(210_100%_56%/0.8)] transition-[width] duration-700"
              style={{ width: `${s.progress}%` }}
              data-testid="level-progress-bar"
            />
          </div>
          <div className="mt-3 flex justify-between text-xs text-muted-foreground">
            <span>{s.level.name}</span>
            <span>{s.next_level?.name || "Liquid Elite"}</span>
          </div>
        </div>

        {offers.length > 0 && (
          <div className="mt-4 grid gap-4 md:grid-cols-2" data-testid="offers-section">
            {offers.map((o) => (
              <div key={o.id} className="border border-primary/30 bg-primary/5 p-6" data-testid={`offer-${o.id}`}>
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary">
                  <Megaphone className="h-3.5 w-3.5" /> {o.kind === "multiplier" ? `${o.multiplier}x points` : "Offer"}
                </div>
                <h3 className="mt-3 text-lg font-semibold">{o.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{o.body}</p>
              </div>
            ))}
          </div>
        )}

        <div className="mt-4 grid gap-4 md:grid-cols-3">
          <div className="border border-border bg-card p-6 md:col-span-2" data-testid="activity-card">
            <h3 className="text-lg font-semibold">Point activity</h3>
            <div className="mt-5 divide-y divide-border">
              {txs.length === 0 && <p className="py-4 text-sm text-muted-foreground">No activity yet. Jump in the city.</p>}
              {txs.slice(0, 10).map((t) => (
                <div key={t.id} className="flex items-center justify-between py-3 text-sm" data-testid={`tx-${t.id}`}>
                  <div>
                    <div>{t.note}</div>
                    <div className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleString()}</div>
                  </div>
                  <span className={`mono ${t.points >= 0 ? "text-primary" : "text-destructive"}`}>
                    {t.points >= 0 ? "+" : ""}
                    {fmt(t.points)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <div className="border border-border bg-card p-6" data-testid="game-account-card">
              <h3 className="text-lg font-semibold">Game account</h3>
              {u.license_id || u.fivem_id || u.steam_id ? (
                <>
                  <p className="mt-2 text-xs text-primary">Linked — your playtime banks automatically.</p>
                  <div className="mono mt-4 space-y-1 text-xs text-muted-foreground">
                    {u.license_id && <div className="truncate">lic {u.license_id}</div>}
                    {u.fivem_id && <div className="truncate">fivem {u.fivem_id}</div>}
                    {u.steam_id && <div className="truncate">steam {u.steam_id}</div>}
                  </div>
                </>
              ) : (
                <p className="mt-2 text-xs text-muted-foreground">
                  Not linked yet. Just hop in the city — we match your Discord automatically and
                  your minutes start banking. Still not linked after a session? Ask an admin.
                </p>
              )}
            </div>
            <div className="border border-border bg-card p-6" data-testid="sessions-card">
              <h3 className="text-lg font-semibold">Recent sessions</h3>
              <div className="mt-4 space-y-2 text-sm">
                {sessions.length === 0 && <p className="text-muted-foreground">No sessions recorded.</p>}
                {sessions.slice(0, 6).map((x) => (
                  <div key={x.id || x.created_at} className="flex justify-between border-b border-border pb-2">
                    <span className="text-muted-foreground">{new Date(x.created_at).toLocaleDateString()}</span>
                    <span className="mono">{hhmm(x.minutes)} · +{x.points}</span>
                  </div>
                ))}
              </div>
            </div>
            <Link to="/rewards">
              <Button variant="outline" className="w-full rounded-none" data-testid="go-rewards-btn">
                Spend points
              </Button>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
